// components/rapport/reportspages/players/PlayerPositionReport.jsx
import { useEffect, useMemo, useState } from "react";
import { useSeason } from "../../../common/SeasonContext";
import { useTheme } from "../../../common/ThemeContext";
import { fetchAuth } from "../../../common/api";
import { filterEnabledPlayers } from "../../../common/playerFilter";
import {
  ResponsiveContainer, ComposedChart, XAxis, YAxis, Tooltip,
  CartesianGrid, Customized, Scatter
} from "recharts";
import { PdfExportProvider, usePdfExport } from "../../pdf/PdfExportContext";
import PlayerPositionTable from "./PlayerPositionTable";
import ChartCapture from "../../pdf/ChartCapture";
import ExportReportMenu from "../../pdf/ExportPlayerPositionReportMenu";

/* =========================
   Config métriques
========================= */
const METRICS = [
  { key:"mechanical_fatigue", label:"Mechanical Fatigue (%)", isPct:true, fmt:v=>Number.isFinite(v)? +(v*100).toFixed(1):null },
  { key:"intensity_index", label:"Intensity (PT)" },
  { key:"mechanical_load_index", label:"Mechanical Load (PT)" },
  { key:"accelerations", label:"Accelerations max (m/s²)" },
  { key:"decelerations", label:"Decelerations max (m/s²)" },
  { key:"sprint_max", label:"Speed max (km/h)" },
  { key:"global_distance", label:"Total Distance (km)" },
];

const POS_COLORS = {
  Defense:"#3B82F6", Midfield:"#22C55E", Forward:"#EF4444", Lateral:"#F59E0B", "N/A":"#9CA3AF"
};

/* =========================
   Aides / UI
========================= */
function PdfLegendSync({ positions, POS_COLORS }) {
  const exporter = usePdfExport();
  useEffect(() => {
    const colorMap = {};
    positions.forEach(pos => { colorMap[pos] = POS_COLORS[pos] || "#666"; });
    exporter.setData("__legend", { colorMap, showTeamAvg:false });
  }, [exporter, positions]);
  return null;
}

function PdfActiveViewSync({ view }) {
  const exporter = usePdfExport();
  useEffect(() => {
    exporter.setData("__activeView", { view });
  }, [view, exporter]);
  return null;
}

function Segmented({ value, onChange, items }) {
  return (
    <div className="inline-flex rounded-lg border overflow-hidden">
      {items.map(it => (
        <button
          key={it.value}
          className={`px-3 py-1 text-sm ${value===it.value ? "bg-sky-600 text-white" : "bg-transparent"}`}
          onClick={() => onChange(it.value)}
        >
          {it.label}
        </button>
      ))}
    </div>
  );
}

function tooltipTheme(dark) {
  return {
    contentStyle: {
      fontSize: 12,
      backgroundColor: dark ? "#111827" : "#ffffff", // gray-900 / white
      border: `1px solid ${dark ? "#374151" : "#E5E7EB"}`, // gray-700 / gray-200
      color: dark ? "#F3F4F6" : "#111827", // gray-100 / gray-900
      borderRadius: 8,
      boxShadow: dark
        ? "0 6px 16px rgba(0,0,0,0.45)"
        : "0 6px 16px rgba(0,0,0,0.15)",
      padding: "8px 10px",
    },
    labelStyle: { color: dark ? "#D1D5DB" : "#111827", marginBottom: 4 },
    itemStyle:  { color: dark ? "#F9FAFB" : "#111827" },
    // wrapperStyle est rarement nécessaire; on le laisse à Recharts
  };
}

/* =========================
   BOX-PLOTS (Q1–Q3 + moustaches)
========================= */
function BoxWhisker({ x, yScale, width, p10, p25, median, p75, p90, color="#3B82F6" }) {
  // Si une valeur manque, on ne dessine pas.
  const ok = [p10, p25, median, p75, p90].every(Number.isFinite);
  if (!ok) return null;

  const cx = x + width / 2;
  const y10 = yScale(p10), y25 = yScale(p25), y50 = yScale(median), y75 = yScale(p75), y90 = yScale(p90);
  // Par sécurité: si l’une des projections devient NaN (scale non prête), on ne dessine pas.
  if (![y10,y25,y50,y75,y90].every(v => Number.isFinite(v))) return null;

  const boxX = x + width * 0.25, boxW = width * 0.5;

  return (
    <g>
      {/* moustaches */}
      <line x1={cx} x2={cx} y1={y10} y2={y25} stroke={color} strokeWidth={1}/>
      <line x1={cx} x2={cx} y1={y75} y2={y90} stroke={color} strokeWidth={1}/>
      {/* cap */}
      <line x1={cx-8} x2={cx+8} y1={y10} y2={y10} stroke={color} />
      <line x1={cx-8} x2={cx+8} y1={y90} y2={y90} stroke={color} />
      {/* boîte Q1–Q3 */}
      <rect
        x={boxX}
        y={Math.min(y25,y75)}
        width={boxW}
        height={Math.abs(y75-y25)}
        fill={color}
        opacity={0.15}
        stroke={color}
      />
      {/* médiane */}
      <line x1={boxX} x2={boxX+boxW} y1={y50} y2={y50} stroke={color} strokeWidth={2}/>
    </g>
  );
}

// --- 2) BoxPlotChart qui filtre + utilise le tableau capturé ----------------
function BoxPlotChart({ byPos, metricKey, color="#3B82F6", dark }) {
  // On construit puis on filtre pour ne garder que les lignes complètes
  const rows = Object.entries(byPos)
    .map(([pos, o]) => ({ pos, ...o.stats?.[metricKey] }))
    .filter(r => [r?.p10, r?.p25, r?.median, r?.p75, r?.p90].every(Number.isFinite));

  // S’il n’y a rien de valide, on affiche une note
  if (!rows.length) {
    return (
      <div className={`rounded-xl border p-3 ${dark? "border-gray-700":"border-gray-200"}`}>
        <div className="text-sm font-semibold mb-1">Distribution by post — {metricKey}</div>
        <div className={dark ? "text-gray-300" : "text-gray-600"}>Not enough data to display a box plot.</div>
      </div>
    );
  }

  return (
    <div className={`rounded-xl border p-3 ${dark? "border-gray-700":"border-gray-200"}`}>
      <div className="text-sm font-semibold mb-2">Distribution by post — {metricKey}</div>
      <div style={{height:280}}>
        <ResponsiveContainer>
          <ComposedChart data={rows} margin={{ top: 10, right: 20, bottom: 10, left: 10 }}>
            <CartesianGrid strokeDasharray="3 3"/>
            <XAxis dataKey="pos"/>
            <YAxis/>
            <Scatter dataKey="median" fill="transparent" shape={() => null} />
            <Tooltip
              formatter={(v, n) => [v, n]}
              labelFormatter={(l) => `Poste: ${l}`}
              {...tooltipTheme(dark)}
            />
            {/* IMPORTANT: on utilise "rows" capturé (et pas props.data) */}
            <Customized
              component={({ xAxisMap, yAxisMap, offset }) => {
                const xMap = Object.values(xAxisMap)[0];
                const yMap = Object.values(yAxisMap)[0];
                const bw   = xMap.scale.bandwidth ? xMap.scale.bandwidth() : 50;
                const left = offset?.left ?? 0;

                return (
                  <g>
                    {rows.map((row) => {
                      const x = xMap.scale(row.pos) + left;
                      return (
                        <BoxWhisker
                          key={row.pos}
                          x={x}
                          yScale={yMap.scale}
                          width={bw}
                          p10={row.p10} p25={row.p25} median={row.median} p75={row.p75} p90={row.p90}
                          color={color}
                        />
                      );
                    })}
                  </g>
                );
              }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

/* =========================
   BEESWARM (Percentiles 0–100)
========================= */
function toPercentile(v, stats) {
  if (v == null || !stats) return null;
  const { p10, p90 } = stats;
  if (!(Number.isFinite(p10) && Number.isFinite(p90)) || p90 <= p10) return null;
  const t = (v - p10) / (p90 - p10);
  return Math.max(0, Math.min(1, t)) * 100; // 0..100
}
function buildBeeswarm(players, values, byPos, metricKey) {
  const out = [];
  for (const p of players) {
    const v = values[p.id]?.[metricKey];
    const stats = byPos[p.position]?.stats?.[metricKey];
    const pct = toPercentile(v, stats);
    if (pct == null) continue;
    const j = (Math.random() - 0.5) * 0.5; // léger jitter
    out.push({ x: p.position || "N/A", y: pct, name: p.name, jitter: j });
  }
  return out;
}
function BeeswarmPercentile({ players, values, byPos, metricKey, dark }) {
  const points = useMemo(() => buildBeeswarm(players, values, byPos, metricKey), [players, values, byPos, metricKey]);
  return (
    <div className={`rounded-xl border p-3 ${dark? "border-gray-700":"border-gray-200"}`}>
      <div className="text-sm font-semibold mb-2">Percentile (vs poste) — {metricKey}</div>
      <div style={{height:280}}>
        <ResponsiveContainer>
          <ComposedChart data={points} margin={{ top: 10, right: 20, bottom: 10, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="x" type="category"/>
            <YAxis domain={[0,100]} ticks={[0,25,50,75,100]} tickFormatter={(v)=>`${v}%`}/>
            <Tooltip
              formatter={(val, _n, payload) => [`${val.toFixed(1)}%`, payload?.payload?.name]}
              labelFormatter={(label) => `Poste: ${label}`}
              {...tooltipTheme(dark)}
            />
            {/* Points avec jitter horizontal (appliqué via shape) */}
            <Scatter
              dataKey="y"
              data={points}
              fill="#10b981"
              shape={BeeswarmDot}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function BeeswarmDot(props) {
  const {
    cx, cy, payload,
    onMouseEnter, onMouseLeave, onMouseMove, // garder les events pour le tooltip
  } = props;

  const j = ((payload?.jitter) ?? 0) * 12; // petit décalage horizontal
  return (
    <circle
      cx={cx + j}
      cy={cy}
      r={3}
      fill="#10b981"
      // on *ne* propage pas ...rest -> évite tooltipPosition & co
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onMouseMove={onMouseMove}
    />
  );
}

/* =========================
   HEATMAP Z-SCORE (joueurs × métriques)
========================= */
function zColor(z, dark = false) {
  if (!Number.isFinite(z)) return { bg: "transparent", fg: undefined };
  const t = Math.max(-2.5, Math.min(2.5, z)) / 2.5; // -1..1
  // Palette douce
  const r = t < 0 ? 245 : Math.round(210 * (1 - t));
  const b = t > 0 ? 245 : Math.round(210 * (1 + t));
  const g = 235 - Math.round(Math.abs(t) * 150);
  const bg = `rgb(${r},${g},${b})`;

  // Light mode: always dark text for readability.
  if (!dark) return { bg, fg: "#111827" }; // near-black

  // Dark mode: keep adaptive contrast (white on darker tiles).
  const toLin = (c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  const lum = 0.2126 * toLin(r) + 0.7152 * toLin(g) + 0.0722 * toLin(b);
  const fg = lum > 0.55 ? "#111111" : "#F9FAFB";
  return { bg, fg };
}

function ZHeatmap({ players, metrics, values, byPos, dark, sort, onSortChange }) {
  return (
    <div className={`rounded-xl border p-3 ${dark? "border-gray-700":"border-gray-200"}`}>
      <div className="text-sm font-semibold mb-2">Heatmap — z-score vs poste</div>
      <div className="overflow-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr>
              <th className="sticky left-0 z-10 bg-inherit px-3 py-2 text-left">Player</th>
              {metrics.map(m => {
                const active = sort?.key === m.key;
                const arrow = active ? (sort.dir === "asc" ? "▲" : "▼") : "";
                return (
                  <th key={m.key} className="px-3 py-2 text-right">
                    <button
                      type="button"
                      onClick={() => onSortChange?.(m.key)}
                      className={[
                        "inline-flex items-center gap-1",
                        "transition-colors underline-offset-2",
                        dark ? "hover:text-white" : "hover:text-gray-900"
                      ].join(" ")}
                      title={`Trier par ${m.label}`}
                    >
                      <span>{m.label}</span>
                      <span className="text-xs opacity-75">{arrow}</span>
                    </button>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {players.map(p => (
              <tr key={p.id} className={dark ? "border-t border-gray-800" : "border-t border-gray-200"}>
                <td className="sticky left-0 z-10 bg-inherit px-3 py-2">{p.name} <span className="text-xs opacity-60">({p.position})</span></td>
                {metrics.map(m => {
                  const st = byPos[p.position]?.stats?.[m.key];
                  const v  = values[p.id]?.[m.key];
                  const z  = (st && Number.isFinite(st.sd) && st.sd > 0 && Number.isFinite(v))
                    ? (v - st.mean) / st.sd : null;
                  const { bg, fg } = zColor(z, dark);
                  const txt = Number.isFinite(v) ? (m.isPct ? `${(v*100).toFixed(1)}%` : v.toFixed(2)) : "—";
                  return (
                    <td key={m.key} className="px-1 py-1 text-right">
                      <div
                          className="rounded-md px-2 py-1"
                          style={{
                            background: bg,
                            color: fg,
                            border: dark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(0,0,0,0.06)",
                          }}
                        >
                          <div className="tabular-nums">{txt}</div>
                          <div className="text-[11px]" style={{ opacity: 0.75 }}>{Number.isFinite(z) ? `z ${z.toFixed(2)}` : ""}</div>
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function buildHeatmapPayload(players, metrics, values, byPos) {
  const cols = metrics.map(m => ({ key: m.key, label: m.label, isPct: !!m.isPct }));
  const rows = players.map(p => {
    const position = p.position || "N/A";
    const cells = metrics.map(m => {
      const st = byPos[position]?.stats?.[m.key];
      const v  = values[p.id]?.[m.key];
      const z  = (st && Number.isFinite(st.sd) && st.sd > 0 && Number.isFinite(v))
        ? (v - st.mean) / st.sd : null;
      return { key: m.key, value: Number.isFinite(v) ? v : null, z };
    });
    return { id: p.id, name: p.name, position, cells };
  });
  return { columns: cols, rows };
}

function PdfHeatmapSync({ players, metrics, values, byPos }) {
  const exporter = usePdfExport();
  useEffect(() => {
    if (!players?.length) return;
    const payload = buildHeatmapPayload(players, metrics, values, byPos);
    exporter.setData("players.heatmap_table", payload);
  }, [players, metrics, values, byPos, exporter]);
  return null;
}


/* =========================
   MOCK enrichi (avec p25/p75)
========================= */
// RNG déterministe par seed
const hashStr = (s) => { let h = 2166136261 >>> 0; for (let i=0;i<s.length;i++){ h^=s.charCodeAt(i); h=Math.imul(h,16777619);} return h>>>0; };
const mulberry32 = (seed) => () => { let t=(seed+=0x6D2B79F5); t=Math.imul(t^(t>>>15),t|1); t^=t+Math.imul(t^(t>>>7),t|61); return ((t^(t>>>14))>>>0)/4294967296; };
const randn = (rng) => { let u=0,v=0; while(u===0)u=rng(); while(v===0)v=rng(); return Math.sqrt(-2*Math.log(u))*Math.cos(2*Math.PI*v); };
const clamp = (x,a,b)=>Math.max(a,Math.min(b,x));
const POS_POOL = ["Defense","Midfield","Forward","Lateral"];
const FIRST = ["Alex","Noah","Liam","Hugo","Evan","Yanis","Theo","Ilan","Sami","Luca","Max"];
const LAST  = ["Martin","Bernard","Dubois","Robert","Leroy","Moreau","Simon","Laurent","Roux","Vincent","Thomas"];
const makeName = (i,pos)=>`${FIRST[i%FIRST.length]} ${LAST[(i*3+POS_POOL.indexOf(pos))%LAST.length]}`;
function sampleMetric(rng, mean, sd, isPct=false) {
  const mu = Number.isFinite(mean) ? mean : 0;
  const s  = Number.isFinite(sd) && sd > 0 ? sd : (isPct ? 0.08 : 0.35);
  const min = isPct ? 0 : mu - 3*s - 1;
  const max = isPct ? 1 : mu + 3*s + 1;
  for (let k=0;k<12;k++){
    const v = mu + randn(rng)*s;
    if (v >= min && v <= max) return isPct ? clamp(v,0,1) : v;
  }
  return isPct ? clamp(mu,0,1) : mu;
}

const USE_FRONT_MOCKS = false;

/* =========================
   PAGE PRINCIPALE
========================= */
export default function PlayersPositionReport() {
  const { season } = useSeason();
  const { isDarkMode } = useTheme();

  const [period, setPeriod] = useState("week");
  const [data, setData] = useState(null);
  const [positionsSel, setPositionsSel] = useState(new Set());
  const [search, setSearch] = useState("");
  const [view, setView] = useState("box"); // 'box' | 'beeswarm' | 'heatmap'
  const [sort, setSort] = useState({ key: null, dir: "desc" }); // dir: 'asc' | 'desc'
  const [useLatest, setUseLatest] = useState(true);
  const [asOf, setAsOf] = useState(null);

  useEffect(() => {
    (async () => {
      const params = new URLSearchParams();
      if (season) params.set("season", season);
      if (useLatest) {
        params.set("latest", "1");
      } else if (asOf) {
        params.set("latest", "0");
        params.set("as_of", asOf); // YYYY-MM-DD
      }
      const res = await fetchAuth(`/players/players-by-position?${params.toString()}`);
      const json = await res.json();

    

      const coerceNumbers = (obj) => {
      const out = {};
      for (const [pid, m] of Object.entries(obj||{})) {
          out[pid] = {};
          for (const [k, v] of Object.entries(m||{})) {
          const n = v === "" || v == null ? null : Number(v);
          out[pid][k] = Number.isFinite(n) ? n : null;
          }
      }
      return out;
      };
      const base = json || {};
      base.values      = coerceNumbers(base.values);
      base.values_prev = coerceNumbers(base.values_prev);
      
      // Filter out disabled players
      const enabledPlayers = filterEnabledPlayers(base.players || []);
      base.players = enabledPlayers;

      setData(json);
      setPositionsSel(new Set(json.positions || []));

      if (!USE_FRONT_MOCKS) return;

      // 1) bases
      const seed = hashStr(`${season}|${period}|players-by-position`);
      const rng  = mulberry32(seed);

      const realPlayers = base.players || [];
      const realValues  = base.values || {};
      const realPrev    = base.values_prev || {};
      const positions0 = Array.from(new Set([...(base.positions||[]), ...POS_POOL]));

      // 2) stats rapides par poste/metric (depuis réel si dispo)
      const metricsKeys = METRICS.map(m=>m.key);
      const byPosStats = {};
      for (const pos of positions0) {
        byPosStats[pos] = byPosStats[pos] || {};
        for (const key of metricsKeys) {
          const arr = realPlayers
            .filter(p => (p.position || "N/A") === pos)
            .map(p => realValues[p.id]?.[key])
            .filter(Number.isFinite);
          if (!arr.length) continue;
          const mean = arr.reduce((a,b)=>a+b,0) / arr.length;
          const sd   = Math.sqrt(arr.reduce((a,b)=>a+(b-mean)*(b-mean),0) / arr.length) || 0.0001;
          const sorted = [...arr].sort((a,b)=>a-b);
          const n = sorted.length;
          const pick = (q)=> sorted[Math.max(0, Math.min(n-1, Math.round((q/100)*(n-1))))];
          byPosStats[pos][key] = { mean, sd, p10: pick(10), p25: pick(25), p75: pick(75), p90: pick(90) };
        }
      }

      

      // 3) vise ~12 joueurs/par poste
      const TARGET_PER_POS = 12;
      const counts = Object.fromEntries(positions0.map(p=>[p,0]));
      for (const p of realPlayers) counts[p.position || "N/A"] = (counts[p.position || "N/A"]||0)+1;

      const extraPlayers = [];
      const extraValues  = {};
      const extraPrev    = {};

      for (const pos of positions0) {
        const need = Math.max(0, TARGET_PER_POS - (counts[pos] || 0));
        for (let i=0;i<need;i++){
          const pid = `mock-${pos}-${i}-${(rng()*1e9|0)}`;
          const name = makeName(i, pos);
          extraPlayers.push({ id: pid, name, position: pos, avatar: null });

          for (const m of METRICS) {
            const stat = byPosStats[pos]?.[m.key];
            const mu = stat?.mean ?? (m.isPct ? 0.35 + rng()*0.3
              : (m.key==="sprint_max"? 28 + rng()*6
              : (m.key==="accelerations"||m.key==="decelerations")? 4 + rng()*2
              : (m.key==="global_distance")? 6 + rng()*4
              : 1.2 + rng()*0.6));
            const sd = stat?.sd   ?? (m.isPct ? 0.10 : 0.40);

            const v  = sampleMetric(rng, mu, sd, !!m.isPct);
            const vp = sampleMetric(rng, mu, sd, !!m.isPct);

            (extraValues[pid] ||= {})[m.key] = v;
            (extraPrev[pid]   ||= {})[m.key] = vp;
          }
        }
      }

      // 4) fusion
      const allPlayers = [...realPlayers, ...extraPlayers];
      const allValues  = { ...realValues, ...extraValues };
      const allPrev    = { ...realPrev,   ...extraPrev };

      

      const perPosFallback = (pos, key, isPct) => {
        // stats issues du réel si dispo
        const stat = byPosStats?.[pos]?.[key];
        if (stat?.mean != null && stat?.sd != null) {
            return { mean: stat.mean, sd: Math.max(0.0001, stat.sd) };
        }
        // sinon, defaults raisonnables par métrique
        const mean = isPct ? 0.45
            : key==="sprint_max"      ? 30
            : key==="accelerations"   ? 4.5
            : key==="decelerations"   ? 4.5
            : key==="global_distance" ? 8
            : key==="intensity_index" ? 3.3
            : key==="mechanical_load_index" ? 2.2
            : 1.2;
        const sd = isPct ? 0.10
            : key==="sprint_max"      ? 1.8
            : key==="accelerations"   ? 0.6
            : key==="decelerations"   ? 0.6
            : key==="global_distance" ? 2.5
            : 0.4;
        return { mean, sd };
        };

        for (const p of allPlayers) {
        const pos = p.position || "N/A";
        const pid = p.id;
        allValues[pid] ||= {};
        allPrev[pid]   ||= {};

        for (const m of METRICS) {
            const key = m.key;
            let cur = allValues[pid][key];
            let prv = allPrev[pid][key];

            // si manquant -> on génère autour d'une loi par poste
            if (!Number.isFinite(cur) || !Number.isFinite(prv)) {
            const { mean, sd } = perPosFallback(pos, key, !!m.isPct);
            if (!Number.isFinite(cur)) allValues[pid][key] = sampleMetric(rng, mean, sd, !!m.isPct);
            if (!Number.isFinite(prv)) allPrev[pid][key]   = sampleMetric(rng, mean, sd, !!m.isPct);
            }

            // normalisation de type (évite "42" string)
            if (Number.isFinite(allValues[pid][key])) allValues[pid][key] = +allValues[pid][key];
            if (Number.isFinite(allPrev[pid][key]))   allPrev[pid][key]   = +allPrev[pid][key];
        }
        }

      // 5) by_position recalculé (avec p25/p75 pour box-plots)
      const positionsAll = Array.from(new Set(allPlayers.map(p=>p.position || "N/A")));
      const byPos = {};
      for (const pos of positionsAll) {
        const stats = {};
        for (const m of METRICS) {
            const arr = allPlayers
            .filter(pp => (pp.position || "N/A") === pos)
            .map(pp => allValues[pp.id]?.[m.key])
            .filter(Number.isFinite);
    
            if (!arr.length) continue;
            const sorted = arr.slice().sort((a,b)=>a-b);
            const n = sorted.length;
            const mean = arr.reduce((a,b)=>a+b,0)/n;
            const sd = Math.sqrt(arr.reduce((a,b)=>a+(b-mean)*(b-mean),0)/n);
            const q = (p)=> sorted[Math.max(0, Math.min(n-1, Math.round((p/100)*(n-1))))];
            stats[m.key] = {
            mean:+mean.toFixed(3),
            median: n%2? sorted[(n-1)/2] : (sorted[n/2-1]+sorted[n/2])/2,
            sd:+sd.toFixed(3),
            p10:+q(10).toFixed(3),
            p25:+q(25).toFixed(3),
            p75:+q(75).toFixed(3),
            p90:+q(90).toFixed(3),
            };
        }
        byPos[pos] = { count: allPlayers.filter(pp => (pp.position||"N/A")===pos).length, stats };
      }

      const teamStats = {};
      for (const m of METRICS) {
      const arr = Object.values(allValues).map(v => v[m.key]).filter(Number.isFinite);
      if (!arr.length) continue;
      const s = arr.slice().sort((a,b)=>a-b);
      const n = s.length;
      const mean = s.reduce((a,b)=>a+b,0)/n;
      const sd = Math.sqrt(s.reduce((a,b)=>a+(b-mean)*(b-mean),0)/n) || 0.0001;
      const q = (p)=> s[Math.max(0, Math.min(n-1, Math.round((p/100)*(n-1))))];
      teamStats[m.key] = { mean, median:n%2?s[(n-1)/2]:(s[n/2-1]+s[n/2])/2, sd, p10:q(10), p25:q(25), p75:q(75), p90:q(90) };
      }
  
      // si un poste n'a rien pour une métrique, copie la baseline équipe
      for (const pos of Object.keys(byPos)) {
      const stats = byPos[pos].stats || {};
      for (const m of METRICS) {
          if (!stats[m.key] && teamStats[m.key]) stats[m.key] = teamStats[m.key];
      }
      byPos[pos].stats = stats;
      }

      setData({
        ...base,
        players: allPlayers,
        positions: positionsAll,
        values: allValues,
        values_prev: allPrev,
        by_position: byPos,
      });
    })();
  }, [season, useLatest, asOf]);

  const players = data?.players || [];
  const values = data?.values || {};
  const byPos = data?.by_position || {};
  const valuesPrev = data?.values_prev || {};
  const positions = data?.positions || [];
  useEffect(() => { if (positions?.length) setPositionsSel(new Set(positions)); }, [positions]);

  const playersFiltered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return players.filter(p => {
      const posOk = positionsSel.has(p.position || "N/A");
      const txtOk = !q || (p.name.toLowerCase().includes(q) || (p.position||"").toLowerCase().includes(q));
      return posOk && txtOk;
    });
  }, [players, positionsSel, search]);

  const playersHeatmapSorted = useMemo(() => {
    if (!sort.key) return playersFiltered;
    const key = sort.key;
    const dir = sort.dir === "asc" ? 1 : -1;
    const copy = [...playersFiltered];
    copy.sort((a, b) => {
      const va = values?.[a.id]?.[key];
      const vb = values?.[b.id]?.[key];
      const aValid = Number.isFinite(va);
      const bValid = Number.isFinite(vb);
      if (aValid && bValid) return (va - vb) * dir;
      if (aValid && !bValid) return -1;  // valeurs présentes avant
      if (!aValid && bValid) return 1;
      // fallback: nom
      return a.name.localeCompare(b.name);
    });
    return copy;
  }, [playersFiltered, values, sort]);

  if (!data) {
    return <div className={`p-6 ${isDarkMode ? "text-gray-200" : "text-gray-700"}`}>Loading…</div>;
  }

  return (
    <div className="p-6 space-y-4">
      <PdfExportProvider>
        <PdfActiveViewSync view={view} />
        {view === "heatmap" && (
          <PdfHeatmapSync
            players={playersHeatmapSorted}
            metrics={METRICS}
            values={values}
            byPos={byPos}
          />
        )}
        <PdfLegendSync positions={positions} POS_COLORS={POS_COLORS} />

        {/* Filtres & Vues */}
        <div className="flex flex-wrap gap-3 items-center">
          <div className="ml-auto flex items-center gap-3">
            <Segmented
              value={view}
              onChange={setView}
              items={[
                { value:"box", label:"Box plots" },
                { value:"beeswarm", label:"Beeswarm" },
                { value:"heatmap", label:"Heatmap z" },
              ]}
            />
            <div className="flex items-center gap-2">
              <label className="text-sm">Use latest</label>
              <input
                type="checkbox"
                checked={useLatest}
                onChange={e => setUseLatest(e.target.checked)}
              />
            </div>
            {!useLatest && (
              <>
                <label className="text-sm">As of</label>
                <input
                  type="date"
                  value={asOf || ""}
                  onChange={e => setAsOf(e.target.value || null)}
                  className={`px-2 py-1 rounded border text-sm ${isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-300"}`}
                />
              </>
            )}
            <ExportReportMenu
              season={season}
              data={{
                ...data,
                __legend: { colorMap: Object.fromEntries((data?.positions||[]).map(p => [p, (POS_COLORS[p]||"#666")])) },
                __meta: { as_of: useLatest ? "latest" : asOf }
              }}
              metrics={METRICS}
              dark={isDarkMode}
            />
          </div>

          <input
            className={`px-2 py-1 rounded border text-sm ${isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-300"}`}
            placeholder="Search player or position…"
            value={search}
            onChange={(e)=>setSearch(e.target.value)}
          />

          <div className="flex flex-wrap gap-1">
            {positions.map(pos => {
              const on = positionsSel.has(pos);
              return (
                <button key={pos}
                  className={`px-2 py-1 text-xs rounded border ${on ? "bg-sky-600 text-white border-sky-600" : (isDarkMode?"bg-gray-800 border-gray-700":"bg-gray-100 border-gray-300")}`}
                  onClick={()=> setPositionsSel(prev => {
                    const next = new Set(prev);
                    next.has(pos) ? next.delete(pos) : next.add(pos);
                    return next;
                  })}
                >
                  {pos}
                </button>
              );
            })}
          </div>
        </div>

        {/* ——— VUES ——— */}
        {view==="box" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {["sprint_max","accelerations","decelerations","mechanical_fatigue"].map(k =>
              <ChartCapture key={k} imageKey={`players.box.${k}`}>
                <BoxPlotChart key={k} byPos={byPos} metricKey={k} dark={isDarkMode}/>
              </ChartCapture>
            )}
          </div>
        )}

        {view==="beeswarm" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {["sprint_max","accelerations","decelerations","mechanical_fatigue"].map(k =>
              <ChartCapture key={k} imageKey={`players.beeswarm.${k}`}>
                <BeeswarmPercentile
                  key={k}
                  players={playersFiltered}
                  values={values}
                  byPos={byPos}
                  metricKey={k}
                  dark={isDarkMode}
                />
              </ChartCapture>
            )}
          </div>
        )}

        {view==="heatmap" && (
          <ChartCapture key={`players.heatmap`} imageKey={`players.heatmap`}>
            <ZHeatmap
              players={playersHeatmapSorted}
              metrics={METRICS}
              values={values}
              byPos={byPos}
              dark={isDarkMode}
              sort={sort}
              onSortChange={(key) => {
                setSort(prev =>
                  prev.key === key
                    ? { key, dir: (prev.dir === "asc" ? "desc" : "asc") }
                    : { key, dir: "desc" }
                );
              }}
            />
          </ChartCapture>
        )}

        {/* Tableau comparatif (garde ton composant amélioré) */}
        <PlayerPositionTable
          players={players}
          positions={positions}
          values={values}
          byPos={byPos}
          prevValues={valuesPrev}
          metrics={METRICS}
          dark={isDarkMode}
        />
      </PdfExportProvider>
    </div>
  );
}
